import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CardModule } from 'primeng/card';
import { ChartModule } from 'primeng/chart';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { AvatarModule } from 'primeng/avatar';
import { TableModule } from 'primeng/table';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SkeletonModule } from 'primeng/skeleton';
import { DashboardService } from '../services/dashboard.service';
import { FsrDashboardData, ScheduleItem } from '../models/dashboard.model';
import { UserService } from '../../../core/services/user.service';
import { User } from '../../../core/services/user.service';
import { InteractionService, IInteraction } from '../../../core/services/interaction.service';
import { PerformanceScoreService, PerformanceMetrics } from '../../../core/services/performance-score.service';

@Component({
  selector: 'app-fsr-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    ChartModule,
    ButtonModule,
    TagModule,
    AvatarModule,
    TableModule,
    SelectModule,
    DatePickerModule,
    TooltipModule,
    ProgressSpinnerModule,
    SkeletonModule
  ],
  templateUrl: './fsr-dashboard.html',
  styleUrl: './fsr-dashboard.scss'
})
export class FsrDashboard implements OnInit {
  dashboardData: FsrDashboardData | null = null;
  currentUser: User | null = null;
  currentUserId: string = '';
  interactions: IInteraction[] = [];
  loading = true;

  // Date filter properties
  dateFilter = {
    period: null as 'today' | 'thisMonth' | 'custom' | null,
    startDate: null as Date | null,
    endDate: null as Date | null
  };

  // Initialize date filter (no filter by default - show all time)
  private initializeDateFilter(): void {
    // No initialization needed - show all time by default
    this.dateFilter.period = null;
    this.dateFilter.startDate = null;
    this.dateFilter.endDate = null;
  }

  periodOptions = [
    { label: 'All Time', value: null },
    { label: 'Today', value: 'today' },
    { label: 'This Month', value: 'thisMonth' },
    { label: 'Custom Range', value: 'custom' }
  ];

  // Date filter status
  isDateFilterActive = false;
  dateFilterDescription = 'All Time';

  // Chart options
  weeklyPerformanceOptions: any;

  constructor(
    private dashboardService: DashboardService,
    private userService: UserService,
    private interactionService: InteractionService,
    private router: Router,
    private performanceService: PerformanceScoreService
  ) {
    this.initializeChartOptions();
  }

  ngOnInit() {
    // Initialize date filter (no filter by default - show all time)
    this.initializeDateFilter();
    this.updateDateFilterStatus();
    this.loadCurrentUser();
    this.loadDashboardData();
  }

  private loadCurrentUser() {
    this.userService.getCurrentUserProfile().subscribe({
      next: (user) => {
        this.currentUser = user;
        this.currentUserId = user._id || '';
        // Load interactions after getting user
        this.loadUserInteractions();
      },
      error: (error) => {
        console.error('Error loading current user:', error);
        this.currentUser = null;
      }
    });
  }

  private loadUserInteractions() {
    if (!this.currentUserId) return;
    
    console.log('Loading interactions for user:', this.currentUserId);
    this.interactionService.getInteractionsByUserId(this.currentUserId).subscribe({
      next: (interactions) => {
        this.interactions = interactions;
        console.log('Loaded interactions:', interactions);
      },
      error: (error) => {
        console.error('Error loading user interactions:', error);
        this.interactions = [];
      }
    });
  }

  private initializeChartOptions() {
    this.weeklyPerformanceOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: false
        }
      },
      scales: {
        x: {
          stacked: false,
        },
        y: {
          beginAtZero: true,
          stacked: false,
          ticks: {
            precision: 0
          }
        }
      }
    };
  }

  private loadDashboardData() {
    this.loading = true;
    const dateFilterParams = this.getDateFilterParams();
    this.dashboardService.getFsrDashboardData(dateFilterParams).subscribe({
      next: (data) => {
        this.dashboardData = data;
        // Override weekly performance with multi-status data if needed
        if (this.dashboardData) {
          this.dashboardData.weeklyPerformance = this.getEnhancedWeeklyPerformanceData();
          
          // Debug contract customer names
          if (this.dashboardData.myContracts) {
            console.log('Contract data received:', this.dashboardData.myContracts);
            this.dashboardData.myContracts.forEach((contract, index) => {
              if (!contract.customerName || contract.customerName === 'Unknown Customer') {
                console.warn(`Contract ${index + 1} missing customer name:`, contract);
              }
            });
          }
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading dashboard data:', error);
        this.loading = false;
        this.dashboardData = null;
      }
    });
  }


  // Enhanced weekly performance data comparing reservations created vs contracts created
  private getEnhancedWeeklyPerformanceData() {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    
    // Calculate actual data if available from backend
    if (this.dashboardData?.myReservations && this.dashboardData?.myContracts) {
      const reservationsData = this.calculateDailyCreations(this.dashboardData.myReservations, 'createdAt');
      const contractsData = this.calculateDailyCreations(this.dashboardData.myContracts, 'startDate');
      
      return {
        labels: days,
        datasets: [
          {
            label: 'Reservations Created',
            data: reservationsData,
            borderColor: '#3B82F6',
            backgroundColor: 'rgba(59, 130, 246, 0.3)',
            fill: false,
            tension: 0.1
          },
          {
            label: 'Contracts Created',
            data: contractsData,
            borderColor: '#10B981',
            backgroundColor: 'rgba(16, 185, 129, 0.3)',
            fill: false,
            tension: 0.1
          }
        ]
      };
    }
    
    // Return empty chart if no data
    return {
      labels: days,
      datasets: [
        {
          label: 'Reservations Created',
          data: [0, 0, 0, 0, 0, 0, 0],
          borderColor: '#3B82F6',
          backgroundColor: 'rgba(59, 130, 246, 0.3)',
          fill: false,
          tension: 0.1
        },
        {
          label: 'Contracts Created',
          data: [0, 0, 0, 0, 0, 0, 0],
          borderColor: '#10B981',
          backgroundColor: 'rgba(16, 185, 129, 0.3)',
          fill: false,
          tension: 0.1
        }
      ]
    };
  }

  // Helper method to calculate daily creations from data
  private calculateDailyCreations(items: any[], dateField: string): number[] {
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const dailyCounts = new Array(7).fill(0);
    
    // Get last 7 days
    const today = new Date();
    const last7Days: Date[] = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      last7Days.push(date);
    }
    
    items.forEach(item => {
      // Fallback to expiryDate if createdAt is not available (for existing data)
      const dateValue = item[dateField] || (dateField === 'createdAt' ? item['expiryDate'] : item[dateField]);
      
      if (!dateValue) {
        console.warn('Item missing date field:', dateField, item);
        return;
      }
      
      const itemDate = new Date(dateValue);
      const dayIndex = last7Days.findIndex(date => 
        date.getDate() === itemDate.getDate() &&
        date.getMonth() === itemDate.getMonth() &&
        date.getFullYear() === itemDate.getFullYear()
      );
      
      if (dayIndex !== -1) {
        dailyCounts[dayIndex]++;
      }
    });
    
    return dailyCounts;
  }

  // Calculate weekly conversion rate from reservations to contracts
  getWeeklyConversionRate(): number {
    if (this.dashboardData?.myReservations && this.dashboardData?.myContracts) {
      // Calculate from actual data
      const reservationsData = this.calculateDailyCreations(this.dashboardData.myReservations, 'createdAt');
      const contractsData = this.calculateDailyCreations(this.dashboardData.myContracts, 'startDate');
      
      const totalReservations = reservationsData.reduce((sum, count) => sum + count, 0);
      const totalContracts = contractsData.reduce((sum, count) => sum + count, 0);
      
      if (totalReservations === 0) return 0;
      return Math.round((totalContracts / totalReservations) * 100);
    }
    
    // Return 0 if no data available
    return 0;
  }

  private getDateFilterParams() {
    // Return undefined if no filter is selected (show all time)
    if (!this.dateFilter.period) {
      return undefined;
    }
    
    if (this.dateFilter.startDate !== null && this.dateFilter.endDate !== null) {
      return {
        period: this.dateFilter.period,
        startDate: this.dateFilter.startDate.toISOString().split('T')[0],
        endDate: this.dateFilter.endDate.toISOString().split('T')[0]
      };
    }
    return { period: this.dateFilter.period };
  }


  getKpiIconClass(icon: string): string {
    return icon;
  }

  getTrendIconClass(direction: string): string {
    switch (direction) {
      case 'up':
        return 'pi pi-arrow-up text-green-500';
      case 'down':
        return 'pi pi-arrow-down text-red-500';
      default:
        return 'pi pi-minus text-gray-500';
    }
  }

  getStatusSeverity(status: string): string {
    switch (status) {
      case 'Active':
        return 'success';
      case 'Inactive':
        return 'danger';
      default:
        return 'info';
    }
  }

  getVisitTypeSeverity(type: string): string {
    switch (type) {
      case 'Sales Call':
        return 'info';
      case 'Demo':
        return 'warning';
      case 'Service Call':
        return 'success';
      case 'Follow-up Visit':
        return 'secondary';
      default:
        return 'info';
    }
  }

  getVisitStatusSeverity(status: string): string {
    switch (status) {
      case 'Completed':
        return 'success';
      case 'Follow-up Required':
        return 'danger';
      case 'In Progress':
        return 'warning';
      case 'Scheduled':
        return 'info';
      default:
        return 'info';
    }
  }

  getScheduleTypeColor(type: string): string {
    switch (type) {
      case 'Service Call':
        return 'border-l-blue-500';
      case 'Follow-up Visit':
        return 'border-l-gray-300';
      case 'Emergency Call':
        return 'border-l-yellow-500';
      case 'Sales Call':
        return 'border-l-green-500';
      case 'Demo':
        return 'border-l-purple-500';
      default:
        return 'border-l-gray-300';
    }
  }

  getSchedulePriorityColor(priority: string): string {
    switch (priority) {
      case 'Urgent':
        return 'text-red-600';
      case 'High':
        return 'text-orange-600';
      case 'Medium':
        return 'text-yellow-600';
      case 'Low':
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  }

  // Date filter methods
  private updateDateFilterStatus(): void {
    const today = new Date();
    let isActive = false;
    let description = '';

    if (!this.dateFilter.period) {
      // No filter - show all time
      isActive = false;
      description = 'All Time';
    } else {
      switch (this.dateFilter.period) {
        case 'today':
          isActive = true;
          if (this.dateFilter.startDate) {
            description = `Today (${this.dateFilter.startDate.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })})`;
          } else {
            description = `Today (${today.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })})`;
          }
          break;
        case 'thisMonth':
          isActive = true; // Show date range for this month
          if (this.dateFilter.startDate && this.dateFilter.endDate) {
            const startStr = this.dateFilter.startDate.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
            const endStr = this.dateFilter.endDate.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
            description = `This Month (${startStr} - ${endStr})`;
          } else {
            const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
            const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
            description = `This Month (${startOfMonth.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })} - ${endOfMonth.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })})`;
          }
          break;
        case 'custom':
          if (this.dateFilter.startDate !== null && this.dateFilter.endDate !== null) {
            isActive = true;
            const startStr = this.dateFilter.startDate.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
            const endStr = this.dateFilter.endDate.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
            description = `Custom Range (${startStr} - ${endStr})`;
          } else {
            description = 'Custom Range (Select dates)';
          }
          break;
      }
    }

    this.isDateFilterActive = isActive;
    this.dateFilterDescription = description;
  }

  onPeriodChange(): void {
    if (this.dateFilter.period === null) {
      // All Time - clear dates
      this.dateFilter.startDate = null;
      this.dateFilter.endDate = null;
    } else if (this.dateFilter.period === 'thisMonth') {
      const today = new Date();
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      this.dateFilter.startDate = startOfMonth;
      this.dateFilter.endDate = endOfMonth;
    } else if (this.dateFilter.period === 'today') {
      const today = new Date();
      this.dateFilter.startDate = new Date(today);
      this.dateFilter.endDate = new Date(today);
    } else if (this.dateFilter.period === 'custom') {
      // Keep existing dates if already set, otherwise leave null
    }
    this.updateDateFilterStatus();
    this.loadDashboardData();
  }

  onDateRangeChange(): void {
    if (this.dateFilter.startDate !== null && this.dateFilter.endDate !== null) {
      this.updateDateFilterStatus();
      this.loadDashboardData();
    }
  }

  clearDateFilter(): void {
    this.dateFilter.period = null;
    this.dateFilter.startDate = null;
    this.dateFilter.endDate = null;
    this.updateDateFilterStatus();
    this.loadDashboardData();
  }

  // Territory methods
  getDisplayedTerritories() {
    if (!this.dashboardData?.assignedTerritories) return [];
    return this.dashboardData.assignedTerritories.slice(0, 10);
  }

  getExtraTerritoriesCount(): number {
    if (!this.dashboardData?.assignedTerritories) return 0;
    return Math.max(0, this.dashboardData.assignedTerritories.length - 10);
  }

  // My Reservations methods
  getUrgentReservationsCount(): number {
    if (!this.dashboardData?.myReservations) return 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return this.dashboardData.myReservations.filter(r => {
      const expiry = new Date(r.expiryDate);
      expiry.setHours(0, 0, 0, 0);
      return expiry.getTime() === today.getTime();
    }).length;
  }

  getUrgentReservationsText(): string {
    const urgentCount = this.getUrgentReservationsCount();
    if (urgentCount > 0) {
      return `${urgentCount} expiring today`;
    }
    return 'All up to date';
  }

  getUrgencyClass(): string {
    return this.getUrgentReservationsCount() > 0 ? 'text-red-600 font-medium' : 'text-gray-500';
  }

  // Today's Schedule methods
  getTodayScheduleCount(): number {
    return this.getTodayScheduleItems().length;
  }

  getTodayScheduleItems(): ScheduleItem[] {
    // Check if backend provides todaySchedule data
    if (this.dashboardData?.todaySchedule && this.dashboardData.todaySchedule.length > 0) {
      return this.dashboardData.todaySchedule;
    }
    // Otherwise use real interaction data
    return this.getTodayScheduleFromInteractions();
  }

  onScheduleItemClick(scheduleItem: any): void {
    // Navigate to appropriate view based on schedule item
    console.log('Schedule item clicked:', scheduleItem);
    // Could navigate to customer view or reservation details
  }

  getUpcomingInteractions() {
    // Use real interaction data
    if (this.interactions && this.interactions.length > 0) {
      const now = new Date();
      return this.interactions
        .filter(interaction => {
          const interactionDate = new Date(interaction.interactionDate);
          return interactionDate > now && 
                 (interaction.interactionStatus === 'upcoming' || interaction.interactionStatus === 'scheduled');
        })
        .map(interaction => ({
          _id: interaction._id || '',
          customerName: this.getBusinessNameFromInteraction(interaction),
          reservationId: typeof interaction.reservationId === 'string' 
            ? interaction.reservationId 
            : interaction.reservationId?._id || '',
          type: this.mapInteractionType(interaction.interactionType),
          status: this.mapInteractionStatus(interaction.interactionStatus),
          date: interaction.interactionDate.toString(),
          scheduledAt: interaction.interactionDate.toString(),
          reservationStatus: typeof interaction.reservationId === 'object' 
            ? interaction.reservationId?.status || 'Unknown'
            : 'Unknown'
        }))
        .sort((a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime())
        .slice(0, 5);
    }
    
    return [];
  }

  // Get today's schedule from real data
  private getTodayScheduleFromInteractions(): ScheduleItem[] {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    return this.interactions
      .filter(interaction => {
        const interactionDate = new Date(interaction.interactionDate);
        return interactionDate >= today && interactionDate < tomorrow;
      })
      .map(interaction => {
        // Get customer name from populated reservation data
        const businessName = this.getBusinessNameFromInteraction(interaction);
        
        return {
          _id: interaction._id || '',
          title: `${businessName} - ${interaction.interactionType}`,
          time: interaction.interactionTime || '10:00',
          type: this.mapInteractionType(interaction.interactionType),
          customer: businessName,
          status: this.mapInteractionStatus(interaction.interactionStatus),
          priority: this.getInteractionPriority(interaction)
        };
      })
      .slice(0, 5);
  }

  // Helper methods for interaction mapping
  private getBusinessNameFromInteraction(interaction: IInteraction): string {
    if (typeof interaction.reservationId === 'object' && interaction.reservationId?.customerId) {
      return interaction.reservationId.customerId.businessName;
    }
    return 'Unknown Customer';
  }

  private mapInteractionType(type: string): 'Service Call' | 'Follow-up Visit' | 'Emergency Call' | 'Sales Call' | 'Demo' {
    // Map backend interaction types to frontend display types
    const typeMapping: { [key: string]: 'Service Call' | 'Follow-up Visit' | 'Emergency Call' | 'Sales Call' | 'Demo' } = {
      // Current types used in forms
      'sales_call': 'Sales Call',
      'demo': 'Demo',
      'service_call': 'Service Call',
      'follow_up': 'Follow-up Visit',
      'emergency': 'Emergency Call',
      // Legacy types (for backward compatibility)
      'phone_call': 'Sales Call',
      'email': 'Follow-up Visit',
      'meeting': 'Service Call',
      'other': 'Service Call'
    };
    return typeMapping[type] || 'Service Call';
  }

  private mapInteractionStatus(status: string): 'Scheduled' | 'In Progress' | 'Completed' | 'Cancelled' {
    const statusMapping: { [key: string]: 'Scheduled' | 'In Progress' | 'Completed' | 'Cancelled' } = {
      'upcoming': 'Scheduled',
      'completed': 'Completed',
      'in_progress': 'In Progress',
      'cancelled': 'Cancelled'
    };
    return statusMapping[status] || 'Scheduled';
  }

  private getInteractionPriority(interaction: IInteraction): 'Low' | 'Medium' | 'High' | 'Urgent' {
    // Determine priority based on interaction type or reservation status
    if (interaction.interactionType === 'emergency') return 'Urgent';
    if (interaction.interactionType === 'follow_up') return 'High';
    return 'Medium';
  }

  // Recent interactions (for Recent Visit History)
  getRecentInteractions() {
    // Use real interaction data from InteractionService
    if (this.interactions && this.interactions.length > 0) {
      return this.interactions
        .map(interaction => ({
          _id: interaction._id || '',
          customerName: this.getBusinessNameFromInteraction(interaction),
          reservationId: typeof interaction.reservationId === 'string' 
            ? interaction.reservationId 
            : interaction.reservationId?._id || '',
          type: this.mapInteractionType(interaction.interactionType),
          status: this.mapInteractionStatus(interaction.interactionStatus),
          date: interaction.interactionDate.toString(),
          time: interaction.interactionTime || '10:00',
          scheduledAt: interaction.interactionDate.toString(),
          reservationStatus: typeof interaction.reservationId === 'object' 
            ? interaction.reservationId?.status || 'Unknown'
            : 'Unknown'
        }))
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, 10);
    }
    
    // Return empty array if no real data
    return [];
  }


  // Formatting methods
  formatCurrency(value: number): string {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }

  formatTime(dateString: string): string {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  formatDateTime(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  isToday(dateString: string): boolean {
    const date = new Date(dateString);
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }

  // Additional severity methods
  getInteractionTypeSeverity(type: string): string {
    switch (type) {
      case 'Sales Call':
        return 'info';
      case 'Demo':
        return 'warning';
      case 'Service Call':
        return 'success';
      case 'Follow-up Visit':
        return 'secondary';
      case 'Emergency Call':
        return 'danger';
      default:
        return 'info';
    }
  }

  getInteractionStatusSeverity(status: string): string {
    switch (status) {
      case 'Completed':
        return 'success';
      case 'Scheduled':
        return 'info';
      case 'In Progress':
        return 'warning';
      case 'Cancelled':
        return 'danger';
      default:
        return 'info';
    }
  }

  getInteractionTypeColor(type: string): string {
    switch (type) {
      case 'Service Call':
        return 'border-l-blue-500';
      case 'Follow-up Visit':
        return 'border-l-gray-300';
      case 'Emergency Call':
        return 'border-l-red-500';
      case 'Sales Call':
        return 'border-l-green-500';
      case 'Demo':
        return 'border-l-purple-500';
      default:
        return 'border-l-gray-300';
    }
  }

  getContractStatusSeverity(status: string): string {
    switch (status) {
      case 'Active':
        return 'success';
      case 'Pending':
        return 'warning';
      case 'Expired':
        return 'danger';
      case 'Draft':
        return 'info';
      default:
        return 'info';
    }
  }

  // Navigation methods
  navigateToCustomerLookup(): void {
    this.router.navigate(['/admin/customer-lookup']);
  }

  navigateToCustomers(): void {
    // Navigate to customers with FSR filter context
    this.router.navigate(['/admin/customers'], {
      queryParams: {
        fsrFilter: 'true',
        fsrId: this.currentUserId
      }
    });
  }

  navigateToViewCustomer(customerId: string, tab?: string): void {
    const queryParams = tab ? { tab } : {};
    this.router.navigate(['/admin/view-customer', customerId], { queryParams });
  }

  navigateToTerritoryManagement(): void {
    this.router.navigate(['/admin/territory-management']);
  }

  navigateToCalendar(): void {
    this.router.navigate(['/admin/calendar']);
  }

  navigateToContracts(): void {
    this.router.navigate(['/admin/contract-management']);
  }

  navigateToReservations(): void {
    this.router.navigate(['/admin/reservations']);
  }

  navigateToInteractions(): void {
    this.router.navigate(['/admin/interactions']);
  }

  navigateToViewContract(contractId: string): void {
    this.router.navigate(['/admin/add-edit-contract/update', contractId]);
  }

  navigateToViewReservation(reservationId: string): void {
    this.router.navigate(['/admin/view-reservation', reservationId]);
  }

  navigateToLogInteraction(interactionId: string): void {
    this.router.navigate(['/admin/log-interaction/update', interactionId]);
  }

  navigateToScheduleInteraction(reservationId: string): void {
    this.router.navigate(['/admin/shedule-interaction', reservationId]);
  }

  navigateToCreateContract(reservationId: string): void {
    this.router.navigate(['/admin/add-edit-contract', reservationId]);
  }

  // Enhanced User Profile Methods
  getCurrentUserName(): string {
    if (this.currentUser) {
      return `${this.currentUser.firstName} ${this.currentUser.lastName}`;
    }
    return 'Loading...';
  }

  getUserInitials(): string {
    if (this.currentUser) {
      const firstName = this.currentUser.firstName || '';
      const lastName = this.currentUser.lastName || '';
      if (firstName && lastName) {
        return firstName[0].toUpperCase() + lastName[0].toUpperCase();
      }
      return firstName.substring(0, 2).toUpperCase();
    }
    return 'U';
  }


  // Enhanced Summary Methods
  getCustomerStatusSummary() {
    const customers = this.dashboardData?.myCustomers || [];
    
    if (!customers || customers.length === 0) {
      return { totalActive: 0, newThisMonth: 0 };
    }
    
    const now = new Date();
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    
    const newThisMonth = customers.filter(customer => {
      const createdDate = new Date(customer.createdAt);
      return createdDate >= oneMonthAgo;
    }).length;

    return {
      totalActive: customers.length,
      newThisMonth
    };
  }

  // Helper methods for template
  getDisplayedCustomers() {
    return this.dashboardData?.myCustomers?.slice(0, 8) || [];
  }

  getDisplayedContracts() {
    return this.dashboardData?.myContracts || [];
  }

  
  getRejectedContractsCount(): number {
    if (!this.dashboardData?.myContracts) return 0;
    return this.dashboardData.myContracts.filter(c => 
      c.status === 'REJECTED' || c.status === 'Rejected' || c.status?.toUpperCase() === 'REJECTED'
    ).length;
  }

  getRejectedContracts() {
    if (!this.dashboardData?.myContracts) return [];
    return this.dashboardData.myContracts.filter(c => 
      c.status === 'REJECTED' || c.status === 'Rejected' || c.status?.toUpperCase() === 'REJECTED'
    );
  }

  getTerminatedContractsCount(): number {
    if (!this.dashboardData?.myContracts) return 0;
    return this.dashboardData.myContracts.filter(c => 
      c.status === 'TERMINATED' || c.status === 'Terminated' || c.status?.toUpperCase() === 'TERMINATED'
    ).length;
  }

  getTerminatedContracts() {
    if (!this.dashboardData?.myContracts) return [];
    return this.dashboardData.myContracts.filter(c => 
      c.status === 'TERMINATED' || c.status === 'Terminated' || c.status?.toUpperCase() === 'TERMINATED'
    );
  }

  // Fix contract value calculation
  getTotalContractValue(): number {
    const contracts = this.dashboardData?.myContracts || [];
    return contracts.reduce((total, contract) => total + contract.contractValue, 0);
  }

  getReservationStatusSummary() {
    if (!this.dashboardData?.myReservations) {
      return { pending: 0, expiringSoon: 0 };
    }

    const now = new Date();
    const threeDaysFromNow = new Date();
    threeDaysFromNow.setDate(threeDaysFromNow.getDate() + 3);

    const pending = this.dashboardData.myReservations.filter(r => 
      r.status === 'Pending' || r.status === 'Interested'
    ).length;

    const expiringSoon = this.dashboardData.myReservations.filter(r => {
      const expiryDate = new Date(r.expiryDate);
      return expiryDate <= threeDaysFromNow && expiryDate >= now;
    }).length;

    return { pending, expiringSoon };
  }

  getActiveTerritoryCount(): number {
    if (!this.dashboardData?.assignedTerritories) return 0;
    return this.dashboardData.assignedTerritories.filter(t => t.status === 'Active').length;
  }

  // Null-safe helper methods
  getTerritoryCount(): number {
    return this.dashboardData?.assignedTerritories?.length || 0;
  }

  getTerritoryDisplayText(): string {
    const count = this.getTerritoryCount();
    return count !== 1 ? 'ies' : 'y';
  }

  // Interest Level Methods
  getInterestLevelSeverity(level: string): string {
    switch (level.toLowerCase()) {
      case 'high':
        return 'success';
      case 'medium':
        return 'warning';
      case 'low':
        return 'secondary';
      case 'very high':
        return 'success';
      default:
        return 'info';
    }
  }

  // Reservation Methods
  isReservationExpiringSoon(expiryDate: string): boolean {
    const expiry = new Date(expiryDate);
    const threeDaysFromNow = new Date();
    threeDaysFromNow.setDate(threeDaysFromNow.getDate() + 3);
    return expiry <= threeDaysFromNow;
  }

  getReservationStatusSeverity(status: string): string {
    switch (status) {
      case 'Pending':
        return 'warning';
      case 'Interested':
        return 'info';
      case 'Converted':
        return 'success';
      case 'Not Interested':
        return 'danger';
      case 'Expired':
        return 'secondary';
      default:
        return 'info';
    }
  }

  // Activity Timeline Methods
  getActivityTimelineIcon(type: string): string {
    switch (type) {
      case 'Sales Call':
        return 'pi pi-phone text-white';
      case 'Demo':
        return 'pi pi-desktop text-white';
      case 'Service Call':
        return 'pi pi-wrench text-white';
      case 'Follow-up Visit':
        return 'pi pi-calendar text-white';
      case 'Emergency Call':
        return 'pi pi-exclamation-triangle text-white';
      default:
        return 'pi pi-circle text-white';
    }
  }

  getActivityTimelineIconClass(type: string): string {
    switch (type) {
      case 'Sales Call':
        return 'bg-green-500';
      case 'Demo':
        return 'bg-purple-500';
      case 'Service Call':
        return 'bg-blue-500';
      case 'Follow-up Visit':
        return 'bg-orange-500';
      case 'Emergency Call':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  }

  getActivityDescription(interaction: any): string {
    const statusText = interaction.status === 'Completed' ? 'completed' : 'scheduled';
    const timeText = interaction.time ? ` at ${interaction.time}` : '';
    return `${statusText} ${interaction.type.toLowerCase()} with ${interaction.customerName}${timeText}`;
  }

  // Action methods for buttons
  onCustomerAction(action: string, customerId?: string, tab?: string): void {
    switch (action) {
      case 'view':
        if (customerId) {
          this.navigateToViewCustomer(customerId, tab);
        }
        break;
      case 'viewAll':
        this.navigateToCustomers();
        break;
    }
  }

  onReservationAction(action: string, reservationId?: string): void {
    switch (action) {
      case 'view':
        if (reservationId) {
          this.navigateToViewReservation(reservationId);
        }
        break;
      case 'viewAll':
        this.navigateToReservations();
        break;
      case 'schedule':
        if (reservationId) {
          this.navigateToScheduleInteraction(reservationId);
        }
        break;
      case 'contract':
        if (reservationId) {
          this.navigateToCreateContract(reservationId);
        }
        break;
    }
  }

  onInteractionAction(action: string, interactionId?: string): void {
    switch (action) {
      case 'view':
      case 'edit':
        if (interactionId) {
          this.navigateToLogInteraction(interactionId);
        }
        break;
    }
  }

  onContractAction(action: string, contractId?: string): void {
    switch (action) {
      case 'view':
        if (contractId) {
          this.navigateToViewContract(contractId);
        }
        break;
      case 'viewAll':
        this.navigateToContracts();
        break;
    }
  }
}
